import { useParams, Link, Navigate } from 'react-router-dom';
import { useState } from 'react';
import StarField from '../home/components/StarField';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import AuthModal from '../home/components/AuthModal';
import { courses } from '../home/components/Courses';
import { useLang } from '../../i18n/LanguageContext';

export default function Course() {
  const { id } = useParams();
  const { t } = useLang();
  const course = courses.find((c) => c.id === Number(id));
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const formUrl = 'https://readdy.ai/api/form/d75441cprit363gl0ug0';
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'signin' | 'signup'>('signup');

  if (!course) {
    return <Navigate to="/" replace />;
  }

  const handleOpenAuth = (tab: 'signin' | 'signup') => {
    setAuthTab(tab);
    setAuthOpen(true);
  };

  const handleAuthSuccess = () => {
    setIsLoggedIn(true);
    setAuthOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formUrl) return;

    setSubmitStatus('submitting');

    const formBody = new URLSearchParams();
    formBody.append('course', t(course.name, courseNameZh || course.name));
    formBody.append('name', formData.name);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('message', formData.message);

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const courseNameZh = (course as unknown as { nameZh?: string }).nameZh;
  const courseDescZh = (course as unknown as { descZh?: string }).descZh;
  const courseDurationZh = (course as unknown as { durationZh?: string }).durationZh;
  const courseLevelZh = (course as unknown as { levelZh?: string }).levelZh;
  const courseObjectivesZh = (course as unknown as { objectivesZh?: string[] }).objectivesZh ?? [];

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#1a0b2e] via-[#2d1b4e] to-[#1a0b2e] overflow-hidden">
      <StarField />
      <Navbar onOpenAuth={handleOpenAuth} />

      <AuthModal
        isOpen={authOpen}
        defaultTab={authTab}
        onClose={() => setAuthOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      <div className="relative z-10 pt-28 pb-24 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Link
            to="/courses"
            className="inline-flex items-center space-x-2 text-[#d4af37] hover:text-[#f4d03f] transition-colors duration-300 mb-6 sm:mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="whitespace-nowrap">{t('Back to Courses', '返回课程列表')}</span>
          </Link>

          {/* Course Header */}
          <div className="bg-[#2d1b4e]/70 backdrop-blur-md rounded-3xl p-5 sm:p-12 border border-[#d4af37]/20 mb-8 sm:mb-12">
            <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-0 sm:space-x-8">
              <div className="w-16 h-16 sm:w-24 sm:h-24 flex items-center justify-center flex-shrink-0">
                <i className={`${course.icon} text-6xl sm:text-8xl text-transparent bg-clip-text bg-gradient-to-br from-[#d4af37] to-[#f4d03f]`}></i>
              </div>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-5xl font-bold text-white mb-3 sm:mb-4">
                  {t(course.name, courseNameZh || course.name)}
                </h1>
                <p className="text-base sm:text-xl text-[#c8b6ff] mb-4 sm:mb-6 leading-relaxed">
                  {t(course.description, courseDescZh || course.description)}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="px-4 py-2 bg-[#1a0b2e] text-[#d4af37] rounded-full border border-[#d4af37]/30 flex items-center space-x-2 text-sm">
                    <i className="ri-time-line"></i>
                    <span className="whitespace-nowrap">{t(course.duration, courseDurationZh || course.duration)}</span>
                  </span>
                  <span className="px-4 py-2 bg-[#1a0b2e] text-[#d4af37] rounded-full border border-[#d4af37]/30 flex items-center space-x-2 text-sm">
                    <i className="ri-bar-chart-line"></i>
                    <span className="whitespace-nowrap">{t(course.level, courseLevelZh || course.level)}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              {/* Course Outline */}
              <div className="bg-[#2d1b4e]/70 backdrop-blur-md rounded-3xl p-5 sm:p-8 border border-[#d4af37]/20">
                <h2 className="text-xl sm:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center space-x-3">
                  <i className="ri-book-open-line text-[#d4af37]"></i>
                  <span>{t('Course Outline', '课程大纲')}</span>
                </h2>
                <div className="space-y-3">
                  {course.outline.map((item, index) => {
                    const outlineItem = item as typeof item & { titleZh?: string; topicsZh?: string[] };
                    return (
                    <div
                      key={index}
                      className="border border-[#d4af37]/20 rounded-xl overflow-hidden transition-all duration-300 hover:border-[#d4af37]/40"
                    >
                      <button
                        onClick={() => setExpandedWeek(expandedWeek === index ? null : index)}
                        className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#1a0b2e]/50 flex items-center justify-between cursor-pointer hover:bg-[#1a0b2e]/70 transition-colors duration-300"
                      >
                        <div className="flex items-center space-x-3 sm:space-x-4">
                          <span className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-[#d4af37] to-[#f4d03f] text-[#1a0b2e] font-bold rounded-lg flex-shrink-0 text-sm">
                            {item.week}
                          </span>
                          <span className="text-white font-semibold text-left text-sm sm:text-base">{t(item.title, outlineItem.titleZh || item.title)}</span>
                        </div>
                        <i className={`ri-arrow-${expandedWeek === index ? 'up' : 'down'}-s-line text-[#d4af37] text-xl sm:text-2xl transition-transform duration-300 flex-shrink-0 ml-2`}></i>
                      </button>
                      {expandedWeek === index && (
                        <div className="px-4 sm:px-6 py-4 bg-[#1a0b2e]/30">
                          <ul className="space-y-2">
                            {item.topics.map((topic, topicIndex) => (
                              <li key={topicIndex} className="flex items-start space-x-3 text-[#c8b6ff]">
                                <i className="ri-checkbox-circle-line text-[#d4af37] mt-1 flex-shrink-0"></i>
                                <span className="text-sm sm:text-base">{t(topic, (outlineItem.topicsZh ?? [])[topicIndex] || topic)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Learning Objectives */}
              <div className="bg-[#2d1b4e]/70 backdrop-blur-md rounded-3xl p-5 sm:p-8 border border-[#d4af37]/20">
                <h2 className="text-xl sm:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center space-x-3">
                  <i className="ri-trophy-line text-[#d4af37]"></i>
                  <span>{t('Learning Objectives', '学习目标')}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {course.objectives.map((objective, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-3 sm:p-4 bg-[#1a0b2e]/30 rounded-xl border border-[#d4af37]/10 hover:border-[#d4af37]/30 transition-all duration-300"
                    >
                      <i className="ri-star-fill text-[#d4af37] mt-1 flex-shrink-0"></i>
                      <span className="text-[#c8b6ff] text-sm sm:text-base">{t(objective, courseObjectivesZh[index] || objective)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Enrollment */}
            <div className="lg:col-span-1">
              <div className="bg-[#2d1b4e]/70 backdrop-blur-md rounded-3xl p-5 sm:p-8 border border-[#d4af37]/20 lg:sticky lg:top-24">
                <h2 className="text-xl sm:text-3xl font-bold text-white mb-5 sm:mb-6 flex items-center space-x-3">
                  <i className="ri-quill-pen-line text-[#d4af37]"></i>
                  <span>{t('Enroll Now', '立即报名')}</span>
                </h2>

                {!isLoggedIn ? (
                  /* Auth Gate */
                  <div className="text-center space-y-6">
                    <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
                      <i className="ri-lock-star-line text-4xl text-[#d4af37]"></i>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-lg mb-2">{t('Join to Enroll', '登录后报名')}</p>
                      <p className="text-[#c8b6ff] text-sm leading-relaxed">
                        {t('Create a free account or sign in to enroll in', '创建免费账户或登录，即可报名')}{' '}
                        <span className="text-[#d4af37]">{t(course.name, courseNameZh || course.name)}</span>{' '}
                        {t('and begin your mystical journey.', '并开启您的神秘学旅程。')}
                      </p>
                    </div>

                    <button
                      onClick={() => handleOpenAuth('signup')}
                      className="w-full py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0d35] font-bold rounded-lg hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2"
                    >
                      <i className="ri-user-add-line text-lg"></i>
                      <span>{t('Sign Up & Enroll', '注册并报名')}</span>
                    </button>

                    <div className="flex items-center space-x-3">
                      <div className="flex-1 h-px bg-[#d4af37]/20"></div>
                      <span className="text-[#c8b6ff]/60 text-xs">{t('or', '或')}</span>
                      <div className="flex-1 h-px bg-[#d4af37]/20"></div>
                    </div>

                    <button
                      onClick={() => handleOpenAuth('signin')}
                      className="w-full py-3 border border-[#d4af37]/40 text-[#d4af37] font-semibold rounded-lg hover:bg-[#d4af37]/10 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2"
                    >
                      <i className="ri-login-circle-line text-lg"></i>
                      <span>{t('Already have an account? Sign In', '已有账户？立即登录')}</span>
                    </button>

                    <div className="pt-2 space-y-2">
                      {[
                        { en: 'Certified upon completion', zh: '完成即可获得认证证书' },
                        { en: 'Lifetime access to materials', zh: '永久访问课程资料' },
                        { en: 'Expert instructor guidance', zh: '专业导师全程指导' },
                      ].map((perk) => (
                        <div key={perk.en} className="flex items-center space-x-2 text-[#c8b6ff]/70 text-sm">
                          <i className="ri-check-line text-[#d4af37] flex-shrink-0"></i>
                          <span>{t(perk.en, perk.zh)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Enrollment Form */
                  <form id="course-enrollment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="course" value={t(course.name, courseNameZh || course.name)} />
                    <div className="flex items-center space-x-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-2">
                      <i className="ri-checkbox-circle-fill text-green-400"></i>
                      <span className="text-green-300 text-sm">{t('You\'re signed in — complete your enrollment below.', '您已登录，请填写以下报名信息。')}</span>
                    </div>

                    <div>
                      <label htmlFor="name" className="block text-[#c8b6ff] mb-2 text-sm">{t('Full Name', '姓名')} *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#1a0b2e]/50 border border-[#d4af37]/20 rounded-lg text-white placeholder-[#c8b6ff]/50 focus:outline-none focus:border-[#d4af37]/50 transition-colors duration-300 text-sm"
                        placeholder={t('Enter your name', '请输入姓名')}
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-[#c8b6ff] mb-2 text-sm">{t('Email Address', '邮箱地址')} *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#1a0b2e]/50 border border-[#d4af37]/20 rounded-lg text-white placeholder-[#c8b6ff]/50 focus:outline-none focus:border-[#d4af37]/50 transition-colors duration-300 text-sm"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-[#c8b6ff] mb-2 text-sm">{t('Phone Number', '联系电话')}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-[#1a0b2e]/50 border border-[#d4af37]/20 rounded-lg text-white placeholder-[#c8b6ff]/50 focus:outline-none focus:border-[#d4af37]/50 transition-colors duration-300 text-sm"
                        placeholder="+44 xxxx xxx xxx"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-[#c8b6ff] mb-2 text-sm">{t('Additional Notes', '备注信息')}</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 bg-[#1a0b2e]/50 border border-[#d4af37]/20 rounded-lg text-white placeholder-[#c8b6ff]/50 focus:outline-none focus:border-[#d4af37]/50 transition-colors duration-300 resize-none text-sm"
                        placeholder={t('Any questions or special requirements? (Max 500 characters)', '有任何问题或特殊要求请告诉我们（最多500字）')}
                      ></textarea>
                      <p className="text-xs text-[#c8b6ff]/50 mt-1">{formData.message.length}/500</p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitStatus === 'submitting'}
                      className="w-full py-4 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-white font-bold rounded-lg hover:shadow-lg hover:shadow-[#d4af37]/50 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitStatus === 'submitting' ? t('Submitting...', '提交中...') : t('Submit Enrollment', '提交报名')}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 text-sm text-center">
                        {t('✓ Enrollment submitted successfully!', '✓ 报名提交成功！')}
                      </div>
                    )}
                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm text-center">
                        {t('✗ Submission failed. Please try again.', '✗ 提交失败，请重试。')}
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
